import { useState, useRef, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mic, Video, Square, Loader2, Trash2, RotateCcw, Check, X } from "lucide-react";

const MAX_SECONDS = 90;

function pickMime(video) {
  const cands = video
    ? ["video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
    : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const c of cands) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(c)) return c;
  }
  return video ? "video/mp4" : "audio/mp4";
}

// Optional mic/camera answers. Typing is always available elsewhere; this never
// scores appearance, voice, accent or camera use — only the confirmed text is used.
export default function AnswerRecorder({ onTranscript }) {
  const [mode, setMode] = useState("idle"); // idle | recording | recorded | transcribing | review
  const [isVideo, setIsVideo] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState("");

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const timerRef = useRef(null);
  const livePreviewRef = useRef(null);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const discard = useCallback(() => {
    cleanupStream();
    if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    blobRef.current = null;
    chunksRef.current = [];
    setMediaUrl(null);
    setTranscript("");
    setSeconds(0);
    setError("");
    setMode("idle");
  }, [cleanupStream, mediaUrl]);

  useEffect(() => () => { cleanupStream(); if (mediaUrl) URL.revokeObjectURL(mediaUrl); }, [cleanupStream, mediaUrl]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const start = async (video) => {
    setError("");
    setIsVideo(video);
    let stream;
    try {
      // Permission is requested only now, after the user actively chose this mode.
      stream = await navigator.mediaDevices.getUserMedia(video ? { audio: true, video: true } : { audio: true });
    } catch (e) {
      setError(
        e && e.name === "NotAllowedError"
          ? `${video ? "Camera/microphone" : "Microphone"} permission was denied. You can simply type your answer instead.`
          : "No microphone/camera was found. You can type your answer instead."
      );
      setMode("idle");
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];
    const mime = pickMime(video);
    const rec = new MediaRecorder(stream, { mimeType: mime });
    recorderRef.current = rec;
    rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunksRef.current.push(ev.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime });
      blobRef.current = blob;
      setMediaUrl(URL.createObjectURL(blob));
      cleanupStream();
      setMode("recorded");
    };
    rec.start();
    setMode("recording");
    setSeconds(0);
    if (video && livePreviewRef.current) {
      livePreviewRef.current.srcObject = stream;
      livePreviewRef.current.play().catch(() => {});
    }
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) { stop(); return MAX_SECONDS; }
        return s + 1;
      });
    }, 1000);
  };

  const transcribe = async () => {
    if (!blobRef.current) return;
    setMode("transcribing");
    try {
      const ext = (blobRef.current.type.includes("mp4") ? "mp4" : "webm");
      const fd = new FormData();
      fd.append("file", blobRef.current, `answer.${ext}`);
      const { data } = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript((data.text || "").trim());
      setMode("review");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Transcription failed.");
      setMode("recorded"); // allow retry or switch to text
    }
  };

  const confirm = () => {
    const t = transcript.trim();
    if (t) onTranscript(t);
    discard(); // raw audio/video is discarded; only confirmed text is kept
    toast.success("Transcript added to your answer");
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <div className="mt-3" data-testid="answer-recorder">
      {mode === "idle" && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-donkey-grey mr-1">Prefer to speak?</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => start(false)}
            className="text-donkey-grey hover:text-donkey-orange border border-donkey-line" data-testid="record-audio-btn">
            <Mic className="w-4 h-4 mr-1.5" /> Record audio
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => start(true)}
            className="text-donkey-grey hover:text-donkey-orange border border-donkey-line" data-testid="record-video-btn">
            <Video className="w-4 h-4 mr-1.5" /> Record video
          </Button>
          {error && <p className="w-full text-xs text-donkey-amber mt-1" data-testid="recorder-error">{error}</p>}
        </div>
      )}

      {mode === "recording" && (
        <div className="rounded-xl border border-donkey-red/40 bg-donkey-red/5 p-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-donkey-warm">
              <span className="w-2.5 h-2.5 rounded-full bg-donkey-red animate-pulse" />
              Recording {isVideo ? "video" : "audio"} · <span className="font-mono" data-testid="recorder-timer">{mmss}</span>
              <span className="text-donkey-grey">/ {String(MAX_SECONDS)}s max</span>
            </span>
            <Button type="button" size="sm" onClick={stop} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="stop-recording-btn">
              <Square className="w-3.5 h-3.5 mr-1.5" /> Stop
            </Button>
          </div>
          {isVideo && <video ref={livePreviewRef} muted playsInline className="mt-3 w-full max-h-56 rounded-lg bg-black" />}
        </div>
      )}

      {(mode === "recorded" || mode === "transcribing") && (
        <div className="rounded-xl border border-donkey-line bg-donkey-bg p-3">
          {isVideo
            ? <video src={mediaUrl} controls playsInline className="w-full max-h-56 rounded-lg bg-black" data-testid="recorder-preview" />
            : <audio src={mediaUrl} controls className="w-full" data-testid="recorder-preview" />}
          <p className="text-[11px] text-donkey-grey mt-2">Your {isVideo ? "video" : "audio"} is only used to create a transcript — it is not stored.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button type="button" size="sm" onClick={transcribe} disabled={mode === "transcribing"}
              className="bg-donkey-orange text-donkey-black font-semibold" data-testid="transcribe-btn">
              {mode === "transcribing" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Transcribing…</> : <>Transcribe</>}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => start(isVideo)} disabled={mode === "transcribing"}
              className="text-donkey-grey border border-donkey-line" data-testid="retake-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Retake
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={discard} disabled={mode === "transcribing"}
              className="text-donkey-grey border border-donkey-line" data-testid="discard-recording-btn">
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </Button>
          </div>
        </div>
      )}

      {mode === "review" && (
        <div className="rounded-xl border border-donkey-orange/40 bg-donkey-bg p-3">
          <p className="text-xs text-donkey-grey mb-2">Check and edit your transcript, then confirm before it's used. Nothing is submitted until you confirm.</p>
          <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={4}
            className="bg-donkey-black border-donkey-line text-donkey-warm" data-testid="transcript-edit" />
          <div className="flex flex-wrap gap-2 mt-3">
            <Button type="button" size="sm" onClick={confirm} disabled={!transcript.trim()}
              className="bg-donkey-orange text-donkey-black font-semibold" data-testid="use-transcript-btn">
              <Check className="w-4 h-4 mr-1.5" /> Use this transcript
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={discard}
              className="text-donkey-grey border border-donkey-line" data-testid="discard-transcript-btn">
              <X className="w-4 h-4 mr-1.5" /> Discard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
